import React, { useState, useCallback, useRef, useContext } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { map, pick } from 'lodash-es';

import ReportPreview from './ReportPreview';
import AskForReferral from './AskForReferral';
import SuccessStep from './SuccessStep';
import { createPdfReport } from '../api';
import { InitialDataContext } from '../context';
import toggleSuccessUrlSearchParam from '../../shared/utils';

const ReportGeneration = ({
  selectedApps,
  reportCharts,
  onAppsChange,
  onChartsChange,
  onStepChange,
}) => {
  const [progress, setProgress] = useState('creating-report');
  const [isProcessing, setProcessing] = useState(false);
  const [errors, setErrors] = useState({});

  const userRef = useRef({});
  const { createReportParams } = useContext(InitialDataContext);


  const handleRedirect = (data) => {
    const shareUrl = data.report_share_url;
    const requestUrl = `/pdf_reports/${data.watchlist_id}/share`;
    window.location.assign(`${window.location.origin}/thank-you-report?request_url=${requestUrl}&share_url=${shareUrl}`);
  };


  const handleFormSubmit = useCallback((token, formParams) => {
    if (formParams.user) userRef.current = formParams.user;
    createPdfReport({
      apps: map(selectedApps, app => pick(app, ['ref_no', 'store', 'name', 'similar_app'])),
      token,
      user: userRef.current,
      ...formParams,
      ...createReportParams,
    }).then(({ data }) => {
      handleRedirect(data);
      setProcessing(false);
      setErrors({});

      toggleSuccessUrlSearchParam('on');
      if (typeof window.gtagReportConversion !== 'undefined') {
        window.gtagReportConversion({ send_to: 'AW-972410676/pz0OCOjq9-ABELSe188D' });
      }
    }).catch(({ response }) => {
      if (response.status === 403) {
        const { ask_for_referral, email_error, share_email_error } = response.data;
        setProgress(ask_for_referral ? 'ask-for-referral' : 'creating-report');
        setErrors(prevState => ({
          ...prevState,
          email: email_error || '',
          shareEmail: share_email_error || '',
        }));
        setProcessing(false);
      } else if (response.status === 429) {
        onStepChange('reached-report-limit');
      } else if ([422, 500].includes(response.status)) {
        onStepChange('error-step');
      }
    });
  }, [selectedApps]);

  const handleResetGeneration = useCallback(() => {
    onAppsChange([]);
    onStepChange('app-selection');
    toggleSuccessUrlSearchParam('off');
  }, []);


  return (
    <div className={cx('report-generation', `${progress}-progress`)}>
      {progress === 'creating-report' && (
        <ReportPreview
          errors={errors}
          isProcessing={isProcessing}
          selectedApps={selectedApps}
          reportCharts={reportCharts}
          setProcessing={setProcessing}
          onAppsChange={onAppsChange}
          onChartsChange={onChartsChange}
          onSubmit={handleFormSubmit}
        />
      )}
      {progress === 'ask-for-referral' && (
        <AskForReferral
          errors={errors}
          isProcessing={isProcessing}
          setProcessing={setProcessing}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
};

ReportGeneration.propTypes = {
  selectedApps: T.array.isRequired,
  reportCharts: T.object.isRequired,
  onAppsChange: T.func.isRequired,
  onChartsChange: T.func.isRequired,
  onStepChange: T.func.isRequired,
};

export default ReportGeneration;
